import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import axios from '../../../utils/axiosConfig';
import { 
  message, 
  Card, 
  Table, 
  Button, 
  Space, 
  Typography, 
  Input, 
  Select, 
  // Row, 
  // Col, 
  Tag, 
  Modal, 
  Popconfirm, 
  Breadcrumb,
  Tabs,
  Checkbox,
  Divider,
  Alert
} from 'antd';
import { 
  CarOutlined, 
  // ReloadOutlined, 
  UserOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  SearchOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const AdminVehicleManagement = () => {
  const { token, currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [geotabAccounts, setGeotabAccounts] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [clientAssignments, setClientAssignments] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedGeotabAccount, setSelectedGeotabAccount] = useState(null);
  const [availableVehicles, setAvailableVehicles] = useState([]); // eslint-disable-line no-unused-vars
  const [assignedVehicles, setAssignedVehicles] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const authHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch clients with vehicle counts
      const clientsRes = await axios.get('/api/client-vehicle-assignments/clients', { headers: authHeader() });
      setClients(clientsRes.data || []);

      // Fetch all Geotab accounts
      const accountsRes = await axios.get('/api/integrations/geotab/accounts', { headers: authHeader() });
      const accounts = Array.isArray(accountsRes.data) ? accountsRes.data : (accountsRes.data?.items || []);
      setGeotabAccounts(accounts);

      // Fetch all client vehicle assignments
      const assignmentsRes = await axios.get('/api/client-vehicle-assignments', { headers: authHeader() });
      setClientAssignments(assignmentsRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      message.error('Failed to fetch vehicle assignment data');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  // Fetch vehicles for a specific Geotab account
  const fetchVehiclesForAccount = useCallback(async (geotabAccountId) => {
    if (!geotabAccountId) return;
    
    setLoading(true);
    try {
      const account = geotabAccounts.find(acc => acc.id === geotabAccountId);
      if (!account) {
        message.error('Geotab account not found');
        return;
      }

      // Fetch devices from this Geotab account
      const { data } = await axios.get(`/api/integrations/geotab/devices?databases=${account.database}`, { headers: authHeader() });
      const devices = Array.isArray(data?.devices) ? data.devices : [];

      // Transform vehicle data
      const transformedVehicles = devices.map((device) => {
        const activeTo = device.activeTo;
        let isActive = true;
        
        if (activeTo) {
          const activeToDate = new Date(activeTo);
          const now = new Date();
          isActive = activeToDate > now || activeToDate.getFullYear() >= 2030;
        }
        
        return {
          id: device.id,
          name: device.name || device.description || `Vehicle ${device.id}`,
          geotab_device_id: device.id,
          serial_number: device.serialNumber || device.vehicleIdentificationNumber || '',
          license_plate: device.licensePlate || '',
          vin: device.vehicleIdentificationNumber || '',
          device_type: device.deviceType || '',
          status: isActive ? 'active' : 'inactive',
          database: account.database,
          account_name: account.name,
          account_id: account.id
        };
      });

      setAllVehicles(transformedVehicles);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      message.error('Failed to fetch vehicles from Geotab');
    } finally {
      setLoading(false);
    }
  }, [geotabAccounts, authHeader]);

  // Get vehicles assigned to a specific client
  const getClientAssignedVehicles = useCallback(async (clientId) => {
    try {
      const { data } = await axios.get(`/api/client-vehicle-assignments/client/${clientId}`, { headers: authHeader() });
      return data || [];
    } catch (err) {
      console.error('Error fetching client assigned vehicles:', err);
      return [];
    }
  }, [authHeader]);

  // Open vehicle assignment modal
  const openAssignModal = async (client) => {
    setSelectedClient(client);
    setSelectedVehicles([]);
    setAssignModalVisible(true);
    
    // Fetch vehicles for this client's Geotab accounts
    const clientVehicles = await getClientAssignedVehicles(client.id);
    setAssignedVehicles(clientVehicles);
  };

  // Handle vehicle assignment
  const handleAssignVehicles = async () => {
    if (!selectedClient || !selectedGeotabAccount || selectedVehicles.length === 0) {
      message.warning('Please select client, Geotab account, and at least one vehicle');
      return;
    }

    setLoading(true);
    try {
      const vehicleAssignments = selectedVehicles.map(vehicleId => {
        const vehicle = allVehicles.find(v => v.id === vehicleId);
        return {
          geotabDeviceId: vehicle.geotab_device_id,
          vehicleName: vehicle.name
        };
      });

      await axios.post('/api/client-vehicle-assignments/assign', {
        clientId: selectedClient.id,
        geotabAccountId: selectedGeotabAccount,
        vehicleAssignments,
        notes: `Assigned by ${currentUser?.full_name || currentUser?.email}`
      }, { headers: authHeader() });

      message.success(`Successfully assigned ${selectedVehicles.length} vehicle(s) to ${selectedClient.full_name || selectedClient.email}`);
      setAssignModalVisible(false);
      fetchData();
    } catch (err) {
      console.error('Error assigning vehicles:', err);
      message.error('Failed to assign vehicles');
    } finally {
      setLoading(false);
    }
  };

  // Remove vehicle assignment
  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await axios.delete(`/api/client-vehicle-assignments/${assignmentId}`, { headers: authHeader() });
      message.success('Vehicle assignment removed successfully');
      fetchData();
    } catch (err) {
      console.error('Error removing assignment:', err);
      message.error('Failed to remove vehicle assignment');
    }
  };

  // Unassign vehicle from modal
  const handleUnassignVehicle = async (vehicle) => {
    try {
      await axios.delete(`/api/client-vehicle-assignments/${vehicle.id}`, { headers: authHeader() });
      message.success(`Unassigned ${vehicle.vehicle_name} from ${selectedClient?.full_name || selectedClient?.email}`);
      
      // Update assigned vehicles list
      setAssignedVehicles(prev => prev.filter(v => v.id !== vehicle.id));
      
      // Refresh data
      fetchData();
    } catch (err) {
      console.error('Error unassigning vehicle:', err);
      message.error('Failed to unassign vehicle');
    }
  };

  // Filter vehicles based on search
  const filteredVehicles = useMemo(() => { // eslint-disable-line no-unused-vars
    return allVehicles.filter(vehicle => {
      const matchesSearch = !searchQuery || 
        vehicle.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.geotab_device_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.serial_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vehicle.license_plate?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [allVehicles, searchQuery]);

  // Get available vehicles for assignment (not already assigned to client)
  const availableVehiclesForAssignment = useMemo(() => {
    if (!selectedClient) return [];
    const assignedDeviceIds = assignedVehicles.map(av => av.geotab_device_id);
    return allVehicles.filter(v => !assignedDeviceIds.includes(v.geotab_device_id));
  }, [allVehicles, assignedVehicles, selectedClient]);

  useEffect(() => {
    if (currentUser?.role === 'superadmin') {
      fetchData();
    }
  }, [fetchData, currentUser?.role]);

  useEffect(() => {
    if (selectedGeotabAccount) {
      fetchVehiclesForAccount(selectedGeotabAccount);
    }
  }, [selectedGeotabAccount, fetchVehiclesForAccount]);

  // Check if user is super admin
  if (currentUser?.role !== 'superadmin') {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Access Denied"
          description="You need super admin privileges to access the Vehicles Management page."
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Client table columns
  const clientColumns = [
    {
      title: 'Client',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{text || record.email}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.email}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        const roleColors = {
          'client': 'green',
          'agency': 'blue', 
          'enterprise': 'purple'
        };
        return (
          <Tag color={roleColors[role] || 'default'}>
            {role?.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Assigned Vehicles',
      dataIndex: 'assigned_vehicle_count',
      key: 'assigned_vehicle_count',
      render: (count) => (
        <Tag color={count > 0 ? 'green' : 'default'}>
          {count || 0} vehicles
        </Tag>
      )
    },
    {
      title: 'Geotab Accounts',
      dataIndex: 'geotab_accounts',
      key: 'geotab_accounts',
      render: (accounts) => accounts ? (
        <Text style={{ fontSize: '12px' }}>{accounts}</Text>
      ) : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            size="small"
            icon={<CarOutlined />}
            onClick={() => openAssignModal(record)}
          >
            Manage Vehicles
          </Button>
        </Space>
      )
    }
  ];

  // Assignment table columns
  const assignmentColumns = [
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.client_email}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Vehicle',
      dataIndex: 'vehicle_name',
      key: 'vehicle_name',
      render: (text, record) => (
        <Space>
          <CarOutlined />
          <div>
            <div>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>ID: {record.geotab_device_id}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Geotab Account',
      dataIndex: 'geotab_account_name',
      key: 'geotab_account_name',
      render: (text, record) => (
        <Space>
          <DatabaseOutlined />
          <div>
            <div>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.geotab_database}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Assigned By',
      dataIndex: 'assigned_by_name',
      key: 'assigned_by_name',
      render: (text, record) => (
        <div>
          <div>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.assigned_by_email}</Text>
        </div>
      )
    },
    {
      title: 'Assigned At',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Remove this vehicle assignment?"
          description="This will remove the vehicle from the client's view."
          onConfirm={() => handleRemoveAssignment(record.id)}
          okText="Remove"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button type="link" danger size="small">
            Remove
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Enterprise' }, { title: 'Vehicles Management' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <CarOutlined style={{ marginRight: 8 }} />
          Vehicles Management
        </Title>
        <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
          Manage vehicle assignments for clients from their Geotab accounts
        </Text>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={<span><TeamOutlined />Clients Overview</span>} key="overview">
            <Table
              columns={clientColumns}
              dataSource={clients}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} clients`
              }}
            />
          </TabPane>

          <TabPane tab={<span><CheckCircleOutlined />All Assignments</span>} key="assignments">
            <Table
              columns={assignmentColumns}
              dataSource={clientAssignments}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} assignments`
              }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Vehicle Assignment Modal */}
      <Modal
        title={`Manage Vehicles for ${selectedClient?.full_name || selectedClient?.email}`}
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedClient(null);
          setSelectedGeotabAccount(null);
          setSelectedVehicles([]);
          setAssignedVehicles([]);
        }}
        onOk={handleAssignVehicles}
        okText="Assign Selected Vehicles"
        width={800}
        okButtonProps={{ disabled: selectedVehicles.length === 0 }}
      >
        {selectedClient && (
          <div>
            {/* Geotab Account Selection */}
            <div style={{ marginBottom: 16 }}>
              <Text strong>Select Geotab Account:</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder="Select a Geotab account"
                value={selectedGeotabAccount}
                onChange={setSelectedGeotabAccount}
              >
                {geotabAccounts.map(account => (
                  <Select.Option key={account.id} value={account.id}>
                    <DatabaseOutlined style={{ marginRight: 8 }} />
                    {account.name} ({account.database})
                  </Select.Option>
                ))}
              </Select>
            </div>

            {selectedGeotabAccount && (
              <>
                {/* Search */}
                <div style={{ marginBottom: 16 }}>
                  <Input
                    placeholder="Search vehicles..."
                    prefix={<SearchOutlined />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    allowClear
                  />
                </div>

                {/* Currently Assigned Vehicles */}
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Currently Assigned Vehicles:</Text>
                  {assignedVehicles.length > 0 ? (
                  <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto' }}>
                    {assignedVehicles.map(vehicle => (
                      <div key={vehicle.id} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        padding: '4px 8px', 
                        borderRadius: 4, 
                        backgroundColor: '#f0f9ff',
                        marginBottom: 4
                      }}>
                        <Tag color="green" style={{ margin: 0 }}>
                          {vehicle.vehicle_name} ({vehicle.geotab_device_id})
                        </Tag>
                        <Popconfirm
                          title="Unassign this vehicle?"
                          description={`Remove ${vehicle.vehicle_name} from ${selectedClient?.full_name || selectedClient?.email}?`}
                          onConfirm={() => handleUnassignVehicle(vehicle)}
                          okText="Unassign"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true, size: 'small' }}
                          cancelButtonProps={{ size: 'small' }}
                        >
                          <Button 
                            type="text" 
                            danger 
                            size="small"
                            icon={<CloseCircleOutlined />}
                          >
                            Unassign
                          </Button>
                        </Popconfirm>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    No vehicles currently assigned
                  </Text>
                )}
                </div>

                <Divider />

                {/* Available Vehicles for Assignment */}
                <div>
                  <Text strong>Available Vehicles to Assign:</Text>
                  {availableVehiclesForAssignment.length > 0 ? (
                    <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 4, padding: 8 }}>
                      <Checkbox.Group
                        value={selectedVehicles}
                        onChange={setSelectedVehicles}
                        style={{ width: '100%' }}
                      >
                        {availableVehiclesForAssignment.map(vehicle => (
                          <div key={vehicle.id} style={{ marginBottom: 8 }}>
                            <Checkbox value={vehicle.id}>
                              <Space>
                                <CarOutlined />
                                <span>{vehicle.name}</span>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  ({vehicle.geotab_device_id})
                                </Text>
                                {vehicle.status === 'active' ? (
                                  <Tag color="green" size="small">Active</Tag>
                                ) : (
                                  <Tag color="red" size="small">Inactive</Tag>
                                )}
                              </Space>
                            </Checkbox>
                          </div>
                        ))}
                      </Checkbox.Group>
                    </div>
                  ) : (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">
                        {allVehicles.length === 0 
                          ? 'No vehicles found in this Geotab account'
                          : 'All vehicles are already assigned to this client'
                        }
                      </Text>
                      {allVehicles.length > 0 && assignedVehicles.length > 0 && (
                        <div style={{ marginTop: 8, padding: 8, background: '#fff2f0', borderRadius: 4, border: '1px solid #ffccc7' }}>
                          <Text type="secondary" style={{ fontSize: '12px', color: '#cf1322' }}>
                            <strong>Note:</strong> All {allVehicles.length} vehicles from this Geotab account are already assigned. 
                            Use the "Currently Assigned Vehicles" section above to unassign vehicles if needed.
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {selectedVehicles.length > 0 && (
                  <div style={{ marginTop: 16, padding: 8, background: '#f0f9ff', borderRadius: 4 }}>
                    <Text strong>
                      Selected {selectedVehicles.length} vehicle(s) for assignment
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminVehicleManagement;
